import { Platform } from "react-native";
import exifr from "exifr";
import { getMediaType, extractCapturedDate, MEDIA_TARGET_UNKNOWN, type IMediaMetadata } from "common247";

/** Singleton promise so the model loads at most once per session. */
let mobilenetModelPromise: Promise<any | null> | null = null;

/**
 * Loads an external script dynamically in the browser DOM.
 * @param {string} src - Path or URL to the script.
 * @returns {Promise<void>} Resolves when the script has loaded.
 */
function loadScript(src: string): Promise<void> {
  return new Promise((resolve, reject) => {
    if (typeof document === "undefined") {
      return reject(new Error("Document DOM not available for script loading"));
    }
    const existing = document.querySelector(`script[src="${src}"]`);
    if (existing) {
      if ((existing as any).dataset?.loaded === "true") return resolve();
      existing.addEventListener("load", () => resolve(), { once: true });
      existing.addEventListener("error", () => reject(new Error(`Failed to load ${src}`)), { once: true });
      return;
    }
    const script = document.createElement("script");
    script.src = src;
    script.async = true;
    script.onload = () => {
      if (script.dataset) script.dataset.loaded = "true";
      resolve();
    };
    script.onerror = () => reject(new Error(`Failed to load ${src}`));
    document.head.appendChild(script);
  });
}

/**
 * Lazily loads TensorFlow.js and the MobileNet v2 (α=0.5) model in the browser.
 * Uses browser-safe dynamic script loading from local public vendor bundles or CDN
 * to prevent Metro bundler dynamic-import unknown module runtime errors.
 *
 * @returns {Promise<any | null>} The loaded MobileNet classifier, or null.
 */
export async function getMobileNet(): Promise<any | null> {
  if (Platform.OS !== "web" || typeof window === "undefined") return null;
  if (!mobilenetModelPromise) {
    mobilenetModelPromise = (async () => {
      try {
        const win = window as any;

        // 1. Ensure TensorFlow.js is loaded
        if (!win.tf) {
          try {
            await loadScript("/vendor/tf.min.js");
          } catch {
            await loadScript("https://cdn.jsdelivr.net/npm/@tensorflow/tfjs@4.22.0/dist/tf.min.js");
          }
        }
        if (win.tf?.ready) {
          await win.tf.ready();
        }

        // 2. Ensure MobileNet is loaded
        if (!win.mobilenet) {
          try {
            await loadScript("/vendor/mobilenet.min.js");
          } catch {
            await loadScript("https://cdn.jsdelivr.net/npm/@tensorflow-models/mobilenet@2.1.1/dist/mobilenet.min.js");
          }
        }

        if (win.mobilenet?.load) {
          return await win.mobilenet.load({ version: 2, alpha: 0.5 });
        }
        return null;
      } catch (err: any) {
        console.warn("[MediaMetadata] MobileNet model loading bypassed:", err?.message || err);
        return null;
      }
    })();
  }
  return mobilenetModelPromise;
}

/**
 * Safely decodes string, buffer, or array from EXIF fields.
 * @param {any} val - Raw EXIF field value.
 * @returns {string | undefined} Sanitized string or undefined.
 */
function sanitizeExifString(val: any): string | undefined {
  if (!val) return undefined;
  if (typeof val === "string") {
    const trimmed = val.trim();
    return trimmed.length > 0 ? trimmed : undefined;
  }
  if (val instanceof Uint8Array || Array.isArray(val)) {
    try {
      const decoded = new TextDecoder("utf-8").decode(new Uint8Array(val)).replace(/\0/g, "").trim();
      return decoded.length > 0 ? decoded : undefined;
    } catch {
      return undefined;
    }
  }
  return undefined;
}

/**
 * Resolves a valid input argument for exifr from various file representations.
 * Handles Blob, File, ArrayBuffer, Uint8Array, and fetchable URLs / data URIs.
 * Returns null if no valid exifr binary or URL input is present.
 *
 * @param {any} file - The candidate file or media asset.
 * @param {string} [contentType] - Optional MIME type for data URIs.
 * @returns {Blob | string | Uint8Array | ArrayBuffer | null} A valid exifr input argument, or null.
 */
function resolveExifrInput(file: any, contentType?: string): Blob | string | Uint8Array | ArrayBuffer | null {
  if (!file) return null;
  if (typeof Blob !== "undefined" && file instanceof Blob) return file;
  if (typeof Blob !== "undefined" && file?.file instanceof Blob) return file.file;
  if (typeof Uint8Array !== "undefined" && (file instanceof Uint8Array || file instanceof ArrayBuffer || file instanceof DataView)) {
    return file;
  }
  if (file?.buffer && (file.buffer instanceof Uint8Array || file.buffer instanceof ArrayBuffer)) {
    return file.buffer;
  }
  if (file?.data && (file.data instanceof Uint8Array || file.data instanceof ArrayBuffer)) {
    return file.data;
  }
  if (typeof file === "string") {
    if (
      file.startsWith("data:") ||
      file.startsWith("blob:") ||
      file.startsWith("http://") ||
      file.startsWith("https://") ||
      file.startsWith("file://")
    ) {
      return file;
    }
    // In Node / test runner environments, string file paths can be resolved by exifr
    if (typeof window === "undefined" && typeof process !== "undefined") {
      return file;
    }
    return null;
  }
  if (typeof file?.uri === "string") {
    if (
      file.uri.startsWith("data:") ||
      file.uri.startsWith("blob:") ||
      file.uri.startsWith("http://") ||
      file.uri.startsWith("https://") ||
      file.uri.startsWith("file://")
    ) {
      return file.uri;
    }
    if (typeof window === "undefined" && typeof process !== "undefined") {
      return file.uri;
    }
    return null;
  }
  if (typeof file?.url === "string") {
    if (file.url.startsWith("data:") || file.url.startsWith("blob:") || file.url.startsWith("http://") || file.url.startsWith("https://") || file.url.startsWith("file://")) {
      return file.url;
    }
    if (typeof window === "undefined" && typeof process !== "undefined") {
      return file.url;
    }
    return null;
  }
  if (typeof file?.downloadUrl === "string") {
    if (file.downloadUrl.startsWith("data:") || file.downloadUrl.startsWith("blob:") || file.downloadUrl.startsWith("http://") || file.downloadUrl.startsWith("https://") || file.downloadUrl.startsWith("file://")) {
      return file.downloadUrl;
    }
    if (typeof window === "undefined" && typeof process !== "undefined") {
      return file.downloadUrl;
    }
    return null;
  }
  if (typeof file?.base64 === "string" && file.base64.trim().length > 0) {
    const mime = contentType || "image/jpeg";
    return file.base64.startsWith("data:") ? file.base64 : `data:${mime};base64,${file.base64}`;
  }
  return null;
}

/**
 * Extracts EXIF metadata (date captured, GPS, dimensions, description) from an image File or asset.
 * Uses exifr when binary/URL data is available, with graceful fallback to asset-provided EXIF properties.
 *
 * @param {any} file - The image File, Blob, or media asset object.
 * @param {string} [contentType] - The MIME type of the file.
 * @returns {Promise<Partial<IMediaMetadata>>} Extracted EXIF fields.
 */
async function extractExifMetadata(file: any, contentType?: string): Promise<Partial<IMediaMetadata>> {
  const assetExif = file?.exif || {};
  let parsedExif: any = null;

  const exifrInput = resolveExifrInput(file, contentType);
  if (exifrInput) {
    try {
      parsedExif = await exifr.parse(exifrInput as any, {
        pick: [
          "DateTimeOriginal",
          "CreateDate",
          "ModifyDate",
          "DateTime",
          "DateTimeDigitized",
          "GPSLatitude",
          "GPSLongitude",
          "ImageWidth",
          "ImageHeight",
          "ExifImageWidth",
          "ExifImageHeight",
          "ImageDescription",
          "description",
          "UserComment",
          "XPComment",
          "XPTitle",
          "Caption",
          "Headline",
          "Make",
          "Model",
          "Software",
          "ISO",
          "FocalLength",
          "ExposureTime",
          "FNumber",
        ],
      });
    } catch (err: any) {
      if (err?.message !== "Invalid input argument") {
        console.warn("[MediaMetadata] EXIF extraction skipped or failed:", err?.message || err);
      }
    }
  }

  const rawDate =
    parsedExif?.DateTimeOriginal ||
    parsedExif?.CreateDate ||
    parsedExif?.ModifyDate ||
    parsedExif?.DateTime ||
    parsedExif?.DateTimeDigitized ||
    assetExif?.DateTimeOriginal ||
    assetExif?.CreateDate ||
    assetExif?.ModifyDate ||
    assetExif?.DateTime ||
    assetExif?.DateTimeDigitized ||
    file?.metadata?.capturedAt ||
    file?.capturedAt ||
    file?.date;
  const capturedAt = extractCapturedDate(file, rawDate);

  const rawLat = parsedExif?.GPSLatitude ?? assetExif?.GPSLatitude ?? file?.gpsLatitude ?? file?.metadata?.gpsLatitude ?? file?.location?.latitude;
  const rawLon = parsedExif?.GPSLongitude ?? assetExif?.GPSLongitude ?? file?.gpsLongitude ?? file?.metadata?.gpsLongitude ?? file?.location?.longitude;
  const lat = typeof rawLat === "number" ? rawLat : undefined;
  const lon = typeof rawLon === "number" ? rawLon : undefined;
  const locationLabel = (lat != null && lon != null)
    ? `${lat.toFixed(4)}°, ${lon.toFixed(4)}°`
    : (file?.metadata?.gpsLocationLabel || file?.location?.text || undefined);

  const description =
    sanitizeExifString(parsedExif?.ImageDescription ?? assetExif?.ImageDescription) ??
    sanitizeExifString(parsedExif?.description ?? assetExif?.description) ??
    sanitizeExifString(parsedExif?.UserComment ?? assetExif?.UserComment) ??
    sanitizeExifString(parsedExif?.Caption ?? assetExif?.Caption) ??
    sanitizeExifString(parsedExif?.Headline ?? assetExif?.Headline) ??
    sanitizeExifString(parsedExif?.XPComment ?? assetExif?.XPComment) ??
    sanitizeExifString(parsedExif?.XPTitle ?? assetExif?.XPTitle) ??
    file?.metadata?.description ??
    file?.description;

  const extraMetadata: Record<string, any> = {
    ...(file?.metadata?.extraMetadata || {}),
  };
  const make = parsedExif?.Make ?? assetExif?.Make;
  if (make) extraMetadata.make = make;
  const model = parsedExif?.Model ?? assetExif?.Model;
  if (model) extraMetadata.model = model;
  const software = parsedExif?.Software ?? assetExif?.Software;
  if (software) extraMetadata.software = software;
  const iso = parsedExif?.ISO ?? assetExif?.ISO;
  if (iso != null) extraMetadata.iso = iso;
  const focalLength = parsedExif?.FocalLength ?? assetExif?.FocalLength;
  if (focalLength != null) extraMetadata.focalLength = focalLength;
  const exposureTime = parsedExif?.ExposureTime ?? assetExif?.ExposureTime;
  if (exposureTime != null) extraMetadata.exposureTime = exposureTime;
  const fNumber = parsedExif?.FNumber ?? assetExif?.FNumber;
  if (fNumber != null) extraMetadata.fNumber = fNumber;

  const width =
    parsedExif?.ExifImageWidth ??
    parsedExif?.ImageWidth ??
    assetExif?.ExifImageWidth ??
    assetExif?.ImageWidth ??
    file?.width ??
    file?.metadata?.width ??
    undefined;
  const height =
    parsedExif?.ExifImageHeight ??
    parsedExif?.ImageHeight ??
    assetExif?.ExifImageHeight ??
    assetExif?.ImageHeight ??
    file?.height ??
    file?.metadata?.height ??
    undefined;

  return {
    capturedAt,
    gpsLatitude: lat,
    gpsLongitude: lon,
    gpsLocationLabel: locationLabel,
    description,
    extraMetadata: Object.keys(extraMetadata).length > 0 ? extraMetadata : undefined,
    width,
    height,
  };
}

/** Maps partial MobileNet class strings to semantic tags. */
const TAG_MAP: Record<string, string[]> = {
  person: ["people", "portrait"],
  face: ["people", "portrait"],
  dog: ["animal", "pet"],
  cat: ["animal", "pet"],
  bird: ["animal", "nature"],
  fish: ["animal", "nature"],
  car: ["vehicle", "transport"],
  bus: ["vehicle", "transport"],
  truck: ["vehicle", "transport"],
  airplane: ["vehicle", "travel"],
  beach: ["outdoor", "nature", "travel"],
  mountain: ["outdoor", "nature", "landscape"],
  forest: ["outdoor", "nature", "landscape"],
  ocean: ["outdoor", "nature", "landscape"],
  food: ["food"],
  pizza: ["food"],
  sandwich: ["food"],
  cake: ["food"],
  building: ["architecture", "urban"],
  church: ["architecture"],
  castle: ["architecture", "history"],
  book: ["document", "reading"],
};

/**
 * Derives semantic tags from MobileNet class label strings.
 * @param {string[]} classes - Top predicted class names.
 * @returns {string[]} Deduplicated semantic tag list.
 */
function deriveTagsFromClasses(classes: string[]): string[] {
  const tags = new Set<string>();
  classes.forEach((cls) => {
    const lower = cls.toLowerCase();
    Object.entries(TAG_MAP).forEach(([keyword, derivedTags]) => {
      if (lower.includes(keyword)) {
        derivedTags.forEach((t) => tags.add(t));
      }
    });
  });
  return Array.from(tags);
}

/**
 * Classifies objects in an image using TensorFlow MobileNet (web only).
 * @param {any} file - Image file, blob, or asset object to classify.
 * @returns {Promise<{objects: string[]; tags: string[]}>} Detected objects and derived tags.
 */
async function classifyImageObjects(file: any): Promise<{ objects: string[]; tags: string[] }> {
  const empty = { objects: [], tags: [] };
  if (Platform.OS !== "web" || typeof window === "undefined") return empty;

  let objectUrl: string | null = null;
  let shouldRevoke = false;
  try {
    let imgSrc: string | null = null;
    if (typeof Blob !== "undefined" && file instanceof Blob) {
      objectUrl = URL.createObjectURL(file);
      shouldRevoke = true;
      imgSrc = objectUrl;
    } else if (typeof Blob !== "undefined" && file?.file instanceof Blob) {
      objectUrl = URL.createObjectURL(file.file);
      shouldRevoke = true;
      imgSrc = objectUrl;
    } else if (typeof file === "string" && (file.startsWith("data:") || file.startsWith("blob:") || file.startsWith("http://") || file.startsWith("https://"))) {
      imgSrc = file;
    } else if (typeof file?.uri === "string" && (file.uri.startsWith("data:") || file.uri.startsWith("blob:") || file.uri.startsWith("http://") || file.uri.startsWith("https://"))) {
      imgSrc = file.uri;
    } else if (typeof file?.url === "string" && (file.url.startsWith("data:") || file.url.startsWith("blob:") || file.url.startsWith("http://") || file.url.startsWith("https://"))) {
      imgSrc = file.url;
    } else if (typeof file?.downloadUrl === "string" && (file.downloadUrl.startsWith("data:") || file.downloadUrl.startsWith("blob:") || file.downloadUrl.startsWith("http://") || file.downloadUrl.startsWith("https://"))) {
      imgSrc = file.downloadUrl;
    } else if (typeof file?.base64 === "string" && file.base64.trim().length > 0) {
      imgSrc = file.base64.startsWith("data:") ? file.base64 : `data:image/jpeg;base64,${file.base64}`;
    }

    if (!imgSrc) return empty;

    const model = await getMobileNet();
    if (!model) return empty;

    const img = new Image();
    await new Promise<void>((resolve, reject) => {
      img.onload = () => resolve();
      img.onerror = () => reject(new Error("Image load failed"));
      img.crossOrigin = "anonymous";
      img.src = imgSrc!;
    });

    const predictions: Array<{ className: string; probability: number }> = await model.classify(img, 5);

    const objects = predictions
      .filter((p) => p.probability > 0.1)
      .map((p) => p.className.split(",")[0].trim());

    const tags = deriveTagsFromClasses(objects);
    return { objects, tags };
  } catch (err) {
    console.warn("[MediaMetadata] Object classification failed:", err);
    const fallbackTags =
      file?.metadata?.tags ||
      (Array.isArray(file?.tags)
        ? file.tags
        : file?.tags && typeof file.tags === "object"
          ? Object.keys(file.tags)
          : []);
    const fallbackObjects = file?.metadata?.objects || [];
    return { objects: fallbackObjects, tags: fallbackTags };
  } finally {
    if (shouldRevoke && objectUrl) URL.revokeObjectURL(objectUrl);
  }
}

/**
 * Extracts metadata from a media file completely offline on the client (web browser).
 *
 * - **Images**: EXIF date/GPS via exifr (or asset metadata) + TensorFlow MobileNet object detection.
 * - **Video/Audio**: Dimensions, duration, and date extraction.
 *
 * Metadata extraction never blocks the upload — errors are silently caught
 * and a valid metadata object is always returned.
 *
 * @param {any} file - The File, Blob, or media asset object.
 * @param {string} contentType - The MIME type of the file.
 * @returns {Promise<IMediaMetadata>} Offline-extracted metadata.
 */
export async function extractMediaMetadata(file: any, contentType: string): Promise<IMediaMetadata> {
  const rawHref =
    (file as any)?.href ||
    (file as any)?.url ||
    (file as any)?.originalUrl ||
    (file as any)?.uri ||
    (file as any)?.webkitRelativePath ||
    file?.name ||
    file?.fileName;
  const metadata: IMediaMetadata = {
    href: typeof rawHref === "string" && rawHref.trim() ? rawHref.trim() : undefined,
    extractedAt: Date.now(),
    mediaType: getMediaType(contentType, file?.name || file?.fileName),
  };

  try {
    if (contentType.startsWith("image/")) {
      const [exifData, classificationData] = await Promise.all([
        extractExifMetadata(file, contentType),
        classifyImageObjects(file),
      ]);

      Object.assign(metadata, exifData);
      if (!metadata.capturedAt) {
        metadata.capturedAt = extractCapturedDate(file);
      }
      if (classificationData.objects.length > 0) {
        metadata.objects = classificationData.objects;
      } else if (file?.metadata?.objects && file.metadata.objects.length > 0) {
        metadata.objects = file.metadata.objects;
      }

      if (classificationData.tags.length > 0) {
        metadata.tags = classificationData.tags;
      } else if (file?.metadata?.tags && file.metadata.tags.length > 0) {
        metadata.tags = file.metadata.tags;
      } else if (Array.isArray(file?.tags) && file.tags.length > 0) {
        metadata.tags = file.tags;
      } else if (file?.tags && typeof file.tags === "object") {
        metadata.tags = Object.keys(file.tags);
      }

      // If no description from EXIF, synthesize an offline description from detected objects and tags, or preserve existing
      if (!metadata.description) {
        if (file?.metadata?.description) {
          metadata.description = file.metadata.description;
        } else if (file?.description) {
          metadata.description = file.description;
        } else if (metadata.objects && metadata.objects.length > 0) {
          const topObjects = metadata.objects.slice(0, 3).join(", ");
          const context = metadata.tags && metadata.tags.length > 0 ? ` (${metadata.tags.slice(0, 3).join(", ")})` : "";
          metadata.description = `Detected ${topObjects}${context}`;
        }
      }
    } else {
      // For video, audio, or other files: extract date from filename / file timestamp
      metadata.capturedAt = extractCapturedDate(file);
      if ((file as any)?.width && !metadata.width) metadata.width = (file as any).width;
      if ((file as any)?.height && !metadata.height) metadata.height = (file as any).height;
      if (typeof (file as any)?.duration === "number" && !metadata.durationMs) {
        metadata.durationMs = Math.round((file as any).duration * 1000);
      }
    }
  } catch (err) {
    console.warn("[MediaMetadata] Top-level extraction error:", err);
  }

  return metadata;
}

/**
 * Determines if a metadata object has any meaningful extracted values worth displaying.
 * @param {IMediaMetadata | undefined} metadata - Media metadata to check.
 * @returns {boolean} True if any displayable field is present.
 */
export function hasExtractedMetadata(metadata: IMediaMetadata | undefined): boolean {
  if (!metadata) return false;
  return Boolean(
    Boolean(metadata.href) ||
    metadata.capturedAt ||
    metadata.gpsLatitude != null ||
    metadata.gpsLongitude != null ||
    metadata.gpsLocationLabel ||
    metadata.description ||
    metadata.width ||
    metadata.height ||
    metadata.durationMs ||
    (metadata.extraMetadata && Object.keys(metadata.extraMetadata).length > 0) ||
    (metadata.objects && metadata.objects.length > 0) ||
    (metadata.tags && metadata.tags.length > 0)
  );
}
